import { Fragment, useMemo } from "react";
import { useReactTable, getCoreRowModel, flexRender } from "@tanstack/react-table";
import { formatPercent } from "utils/formatNumber";
import getMetricSpec from "views/merchandise/metrics";
import { getTrendColor } from "./utils";

const Table = ({
  title = "",
  columns,
  data,
  showPrevPeriodTrendRow = false,
  showYoyTrendRow = false,
  marketPlace,
  trendRowDescription = false,
  titleColWidth = 30,
  noDataMessage = "",
}) => {
  const columnDefs = useMemo(() => {
    return columns?.map((col) => {
      const metricSpec = getMetricSpec(marketPlace, col.accessor);
      return {
        ...col,
        header: col.Header,
        accessorKey: col.accessor,
        cell: ({ row }) => {
          if (col.customCell) {
            return col.customCell(row.original);
          }
          return metricSpec.name ?
              <span>{metricSpec.formatter(metricSpec.accessor(row.original))}</span>
            : row.original[col.accessor];
        },
      };
    });
  }, [columns, marketPlace]);

  const table = useReactTable({
    columns: columnDefs,
    data,
    getCoreRowModel: getCoreRowModel(),
  });

  const buildTrendRow = (row, compareAccessorKey, rowLabel = null, isLastRow = false) => {
    return (
      <tr className="trend-row">
        {row.getVisibleCells().map((cell, colIndex) => {
          const columnDef = cell.column.columnDef;
          const compareAccessor = columnDef[compareAccessorKey];
          const compareValue = row.original[compareAccessor];

          if (columnDef.customCompareCell && typeof columnDef.customCompareCell === "function") {
            return (
              <td
                key={cell.id}
                style={{
                  textAlign: colIndex ? "center" : "left",
                  width: colIndex ? `${70 / columns.length}%` : "auto",
                }}
                className={`py-2 ${isLastRow ? "report-td-border-bottom" : "report-td-border-none"}`}
              >
                {columnDef.customCompareCell(row.original)}
              </td>
            );
          }

          return (
            <td
              key={cell.id}
              style={{
                textAlign: colIndex ? "center" : "left",
                width: colIndex ? `${70 / columns.length}%` : "auto",
                color: getTrendColor(compareValue, cell.column.id),
              }}
              className={`py-2 ${isLastRow ? "report-td-border-bottom" : "report-td-border-none"}`}
            >
              {compareValue ?
                formatPercent(compareValue, 2, true)
              : colIndex === 0 ?
                rowLabel
              : "-"}
            </td>
          );
        })}
      </tr>
    );
  };

  return (
    <>
      {title && (
        <div
          style={{ marginTop: "20px", marginBottom: "10px", textAlign: "left", fontSize: "17px", fontWeight: "bold" }}
        >
          {title}
        </div>
      )}

      <table className="box-stats-data-table" style={{ width: "100%", tableLayout: "auto" }}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((column, i) => {
                return (
                  <th
                    key={column.id}
                    style={{
                      textAlign: i ? "center" : "left",
                      width: i ? `${(100 - titleColWidth) / columns.length}%` : `${titleColWidth}%`,
                    }}
                  >
                    {flexRender(column.column.columnDef.header, column.getContext())}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {data.length === 0 && noDataMessage ?
            <tr>
              <td
                colSpan={columns?.length}
                className="text-center text-muted"
                style={{ paddingTop: "75px", paddingBottom: "75px" }}
              >
                {noDataMessage}
              </td>
            </tr>
          : null}
          {table.getRowModel().rows.map((row) => (
            <Fragment key={row.id}>
              <tr>
                {row.getVisibleCells().map((cell, colIndex) => (
                  <td
                    key={cell.id}
                    style={{
                      textAlign: colIndex ? "center" : "left",
                      width: colIndex ? `${(100 - titleColWidth) / columns.length}%` : `${titleColWidth}%`,
                    }}
                    className={`py-2 ${!showPrevPeriodTrendRow && !showYoyTrendRow ? "report-td-border-bottom" : "report-td-border-none"}`}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
              {showPrevPeriodTrendRow &&
                buildTrendRow(row, "compareMetricId", trendRowDescription ?? "vs. Prev", !showYoyTrendRow)}
              {showYoyTrendRow && buildTrendRow(row, "yoyCompareMetricId", trendRowDescription ?? "YoY", true)}
            </Fragment>
          ))}
        </tbody>
      </table>
    </>
  );
};

export default Table;
